import { ReactNode, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import PaginationInMemory from "../shared-components/PaginationInMemory";
import SkeletonGrid from "../shared-components/SkeletonGrid";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { useCancel360 } from "../api/Cancel";
import { usePermissionValidator } from "../api/UserPermissionValidators";

import {
  FilterSortHeader,
  GenericObject,
  WithFilterSortHeaderBaseProps,
  WithFilterSortHeaderInjectedProps,
} from "./FilterSortHeader";
import CheckBox from "../shared-components/CheckBox";

export type ThreeSixtyListBaseProps = {
  /** Whether the user has permission to view this page */
  hasPermission: boolean;
  /** Function to render each row */
  renderRow: (
    row: GenericObject,
    cancel: string[],
    toggleCancel: (value: string) => number,
    forceReload: () => void
  ) => ReactNode;
  /** Number of skeleton rows to show while loading (default: 7) */
  skeletonRows?: number;
};

export const ThreeSixtyListBase = (
  props: ThreeSixtyListBaseProps &
    WithFilterSortHeaderBaseProps &
    WithFilterSortHeaderInjectedProps
) => {
  const {
    hasPermission,
    renderRow,
    skeletonRows = 7,
    header_definitions,
    sortField,
    reverse,
    setSortBy,
    fullData,
    fullDataLoading,
    activeFilters,
    setFilters,
    activeData,
    filteredData,
    page,
    page_size,
    setPage,
    clearFilters,
    forceReload,
  } = props;

  const [cancel, setCancel] = useState<string[]>([]);
  const { mutateAsync: cancel360, isLoading: isCancelling } = useCancel360();
  const cancelRef = useRef<HTMLInputElement>(null);
  const perms = usePermissionValidator();

  function toggleCancel(value: string): number {
    if (cancel.includes(value)) {
      const removed: string[] = cancel.filter((x) => x !== value);
      setCancel(removed);
      return -1;
    }
    const updated = [...cancel, value];
    setCancel(updated);
    return 1;
  }

  function toggleCancelAll() {
    if (cancel.length === (fullData?.length ?? 0)) {
      setCancel([]);
    } else {
      const allValues = fullData?.map((row) => row.request_uuid) ?? [];
      setCancel(allValues);
    }
  }

  async function handleCancelClick() {
    if (cancel.length === 0) return;
    try {
      await cancel360(cancel);
      setCancel([]);
      forceReload();
    } catch (error) {
      // Error handling is done by the mutation
    }
  }

  useEffect(() => {
    if (cancelRef.current && (fullData?.length ?? 0) > 0) {
      // indeterminate can't be set via props, so we need to set it via ref
      cancelRef.current.indeterminate =
        cancel.length > 0 && cancel.length < (fullData?.length ?? 0);
    }
  }, [cancelRef, fullData, cancel]);

  if (!hasPermission) {
    return <Navigate to="/unauthorized" replace />;
  }

  const hasData = fullData && fullData.length > 0;

  return (
    <div>
      {/* Refresh Button */}
      <div className="absolute right-4 -top-8">
        <ArrowPathIcon
          className="w-5 stroke-green-500"
          onClick={() => forceReload()}
        />
      </div>

      {/* Table Header */}
      <div className="flex gap-2 text-gray-500 text-xs font-medium bg-gray-50 p-2 border rounded-t-xl border-gray-300 uppercase">
        {header_definitions.map((headerDef, i) => (
          <FilterSortHeader
            key={i}
            headers={headerDef}
            sortField={sortField}
            reverse={reverse}
            sortCallback={setSortBy}
            data={fullData || []}
            activeFilters={activeFilters}
            setFilterCallback={setFilters}
            overrideDisplay={
              i === 0 && perms.canCancel360 && cancel.length > 0 ? (
                <div className="flex items-center gap-2 justify-start">
                  <CheckBox
                    checked={cancel.length === (fullData?.length ?? 0)}
                    ref={cancelRef}
                    onChange={toggleCancelAll}
                  />
                  <Button
                    text={`Cancel (${cancel.length}/${fullData?.length ?? 0})`}
                    type={ButtonTypes.default}
                    colorType={ButtonColorTypes.cancel}
                    onClick={handleCancelClick}
                    disabled={cancel.length === 0 || isCancelling}
                    className="text-xs"
                  />
                </div>
              ) : undefined
            }
          />
        ))}
      </div>

      {/* Table Body */}
      <div className="bg-white border rounded-b-xl border-gray-300">
        {fullDataLoading ? (
          <div className="p-2">
            <SkeletonGrid numberOfRows={skeletonRows} />
          </div>
        ) : !hasData ? (
          <div className="p-2">no data found</div>
        ) : (
          <div className="text-xs">
            {activeData.map((row) =>
              renderRow(row, cancel, toggleCancel, forceReload)
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!fullDataLoading && hasData && (
        <PaginationInMemory
          currentPage={page}
          pageSize={page_size}
          totalCount={filteredData.length || 0}
          unfilteredCount={fullData?.length || 0}
          clearFilters={clearFilters}
          pageChange={(idx: number) => setPage(idx)}
          showDescription={true}
          activeStyle="bg-green-50"
        />
      )}
    </div>
  );
};
